import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { supabase } from '../lib/supabase';
import { trackEvent, trackMeta, cruiseParams, readAttribution } from '../lib/analytics';
import { formatCardDate, displayDeck, cruiseLabel } from '../components/shared/helpers';
import HoldTimer from '../components/shared/HoldTimer';
import { sendTicketWhatsApp, generateAndSendInvoice, toDdMmYyyy } from '../lib/postBooking';

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
const GENERIC_ERROR = 'Something went wrong starting your payment. Please try again.';
// Shown once payment has gone through but the booking couldn't be finalised —
// never invites a second payment.
const VERIFY_FAIL_MSG = "We couldn't verify your payment. If any amount was deducted, please contact us with your details and we'll sort it out — do not pay again.";
const BOOK_FAIL_MSG = 'Your payment was received, but we could not finalise your booking automatically. Our team has your payment on record and will confirm your booking or arrange a refund shortly. Please do not pay again.';

// Below this many seconds left on the hold, we refuse to open checkout — there
// is not enough time to finish paying, so we send the guest back to reselect
// (which gives a fresh 10-minute hold) rather than risk paying against a hold
// that expires mid-checkout.
const MIN_SECONDS_TO_PAY = 120;

export default function Payment() {
  const {
    customerId, holdId, holdSeconds,
    selectedDate, passengers, selectedCruise,
    paxDetails, fullName, email, phone,
    setBookingRef, setPaidAmount, setHoldId, setHoldExpiresAt, setHoldExpiredNotice,
    fading, go, exitFlow,
  } = useBooking();


  // phase: 'ready' | 'working' | 'verifying' | 'booking' | 'seating_failed' | 'failed'
  const [phase, setPhase] = useState('ready');
  const [errorMsg, setErrorMsg] = useState('');
  const [scriptReady, setScriptReady] = useState(false);

  // Load the Razorpay Checkout script once for this screen.
  useEffect(() => {
    if (window.Razorpay) { setScriptReady(true); return; }
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      if (existing.getAttribute('data-loaded')) setScriptReady(true);
      else existing.addEventListener('load', () => setScriptReady(true));
      return;
    }
    const s = document.createElement('script');
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => { s.setAttribute('data-loaded', 'true'); setScriptReady(true); };
    s.onerror = () => setScriptReady(false);
    document.body.appendChild(s);
  }, []);

  // Display-only pricing (the server independently recomputes the authoritative
  // amount from the pricing table; nothing here is trusted for charging).
  const priceAdult = selectedCruise?.priceAdult ?? 0;
  const priceChild = selectedCruise?.priceChild ?? 0;
  const grandTotal = passengers.adults * priceAdult + passengers.children * priceChild;

  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');

  const lowTime = holdSeconds > 0 && holdSeconds < MIN_SECONDS_TO_PAY;

  // Verify the signature, then (on success) write the real booking. The whole
  // post-checkout flow lives here so the user never lands in an ambiguous state.
  const finalize = useCallback(async (resp) => {
    setPhase('verifying');
    try {
      const { data: vData, error: vErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'verify',
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        },
      });
      if (vErr || !vData || !vData.verified) {
        setErrorMsg(VERIFY_FAIL_MSG);
        setPhase('failed');
        return;
      }

      // Verified → write the booking (atomic seat claim + booking + guests + ref).
      setPhase('booking');
      const { data: bData, error: bErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'book',
          razorpay_order_id: resp.razorpay_order_id,
          customerId,
          passengers: paxDetails,
          bookingName: fullName,
          guestPhone: phone,
          guestEmail: email,
        },
      });

      if (!bErr && bData && bData.booked) {
        // Real reference for the Confirmation screen; stop the hold countdown so
        // a confirmed guest is never bounced back to availability.
        const reference = bData.reference || '';
        setBookingRef(reference);
        setHoldExpiredNotice(false);
        setHoldId(null);
        setHoldExpiresAt(null);

        // Auto-deliver the boarding-pass ticket to the customer's WhatsApp. We
        // have the real booking id + reference now. Build the booking object the
        // generator expects from BookingContext + the book result. Fire-and-
        // forget: NOT awaited, so a slow/failed send never blocks or breaks the
        // confirmation screen, and a ticket failure can never undo the booking.
        const isoDate = selectedDate;
        sendTicketWhatsApp(
          {
            id: bData.bookingId,
            booking_name: fullName,
            cruise_type: selectedCruise.cruiseName,
            deck: selectedCruise.deckLabel,
            date: toDdMmYyyy(isoDate),
            adults: passengers.adults,
            children: passengers.children,
            infants: passengers.infants,
            tour_leaders: 0,
            guest_phone: phone,
            partner_phone: null,
          },
          reference,
        );

        // Auto-generate the tax invoice for this confirmed booking, then build the
        // invoice PDF and WhatsApp it to the customer. Fire-and-forget, exactly like
        // the ticket send: NOT awaited, all failures caught and logged only, so the
        // invoice pipeline can never block, delay, or undo the confirmation. The
        // generate-invoice edge function is idempotent (a retry returns the existing
        // invoice, no duplicate).
        generateAndSendInvoice(bData.bookingId, phone, toDdMmYyyy(isoDate))
          .catch((err) => console.error('Invoice generate/send failed (booking confirmed):', err));

        go('/confirmation');
        return;
      }

      if (bData && bData.seating_failed) {
        setPhase('seating_failed');
        return;
      }

      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    } catch (e) {
      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    }
  }, [customerId, paxDetails, fullName, phone, email, selectedDate, selectedCruise, passengers, setBookingRef, setHoldId, setHoldExpiresAt, setHoldExpiredNotice, go]);

  // Start payment: re-check the hold has enough time, create an order on the
  // server, then open Razorpay Checkout with the server-returned order.
  const handlePay = async () => {
    if (phase === 'working' || phase === 'verifying' || phase === 'booking') return;
    if (holdSeconds < MIN_SECONDS_TO_PAY) return; // safeguard — handled by the lowTime view
    if (!scriptReady || !window.Razorpay) {
      setErrorMsg('The payment window is still loading. Please try again in a moment.');
      return;
    }

    setErrorMsg('');
    setPhase('working');
    try {
      const { data, error } = await supabase.functions.invoke('payment', {
        // passengers/bookingName/guestPhone/guestEmail are a NON-monetary
        // snapshot the server stores on the payments row, so the Razorpay
        // webhook can complete this booking with the real guest list if we
        // never make it back from checkout (UPI app switch, closed tab).
        // attribution travels the same way: the first-touch campaign (ag_src)
        // and the pixel's _fbp/_fbc cookies are stored on the payments row at
        // order time because the webhook, which creates most bookings, has no
        // browser cookies to read.
        body: {
          action: 'create-order',
          customerId,
          holdId,
          passengers: paxDetails,
          bookingName: fullName,
          guestPhone: phone,
          guestEmail: email,
          attribution: readAttribution(),
        },
      });

      if (data && data.error === 'hold_expired') {
        go('/availability');
        return;
      }
      if (error || !data || data.error || !data.orderId) {
        setPhase('ready');
        setErrorMsg(GENERIC_ERROR);
        return;
      }

      // The amount the guest will actually be charged (server-computed paise
      // → rupees), kept for the Purchase event on Confirmation.
      setPaidAmount(Number(data.amount) / 100);

      const rzp = new window.Razorpay({
        key: data.keyId,            // public checkout key, from the server
        amount: data.amount,        // server-computed paise
        currency: data.currency,
        order_id: data.orderId,
        name: 'Alfresco Grand',
        description: `${cruiseLabel(selectedCruise, 'short')} · ${displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)}`,
        prefill: { name: fullName, email, contact: phone },
        theme: { color: '#C9A84C' },
        handler: (resp) => { finalize(resp); },
        modal: {
          // Dismissed without paying — keep them here with the hold intact to retry.
          ondismiss: () => { setPhase((p) => (p === 'working' ? 'ready' : p)); },
        },
      });
      rzp.on('payment.failed', () => {
        setPhase('ready');
        setErrorMsg('Your payment could not be completed. Please try again.');
      });
      // Funnel step 5 — checkout opened. Value is the server-computed order
      // amount (paise → rupees); nothing from the prefill block is sent.
      trackEvent('payment_started', {
        ...cruiseParams(selectedCruise, passengers),
        value: Number(data.amount) / 100,
        currency: data.currency || 'INR',
      });
      trackMeta('AddPaymentInfo', {
        value: Number(data.amount) / 100,
        currency: data.currency || 'INR',
        content_name: selectedCruise?.cruiseName || '',
      });
      rzp.open();
    } catch (e) {
      setPhase('ready');
      setErrorMsg(GENERIC_ERROR);
    }
  };

  // Guard — an active hold (and its cruise + date) must be present. A refresh
  // clears in-memory state by design, so fall back to Available Cruises.
  if (!selectedCruise || selectedDate === null || !holdId) return <Navigate to="/availability" replace />;

  // ── Seating could not be secured after a successful payment ─────────────────
  if (phase === 'seating_failed') {
    return (
      <div className={`w-full max-w-[560px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.2rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          Payment received
        </h1>
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-5">
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed mb-3">
            Your payment was successful, but we couldn't secure seating for your party. No booking has been made and you will not be charged again.
          </p>
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed">
            Our team will contact you shortly to arrange a full refund. We're sorry for the inconvenience.
          </p>
        </div>
      </div>
    );
  }

  // ── Payment couldn't be verified / booking couldn't be finalised ────────────
  if (phase === 'failed') {
    return (
      <div className={`w-full max-w-[560px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.2rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          We hit a snag
        </h1>
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-5">
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed">
            {errorMsg}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-[600px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Back arrow */}
        <button
          type="button"
          onClick={() => go('/passenger-details')}
          className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] mb-4"
          aria-label="Go back"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Booking-hold countdown */}
        <HoldTimer />

        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.01em] text-white mb-5">
          Payment
        </h1>

        {/* Booking summary */}
        <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-5">
          {[
            ['Date', formatCardDate(selectedDate)],
            ['Cruise', cruiseLabel(selectedCruise)],
            ['Deck', displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)],
            ['Time', selectedCruise.time],
            ['Passengers', paxSummary],
          ].map(([label, value], i) => (
            <div key={label} className={`flex items-start justify-between gap-6 py-2.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                {label}
              </span>
              <span className="font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words">
                {value}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 pt-3 mt-1 border-t border-[rgba(212,168,67,0.25)]">
            <span className="font-dm-sans text-sm font-medium text-white">Total <span className="text-white/55 font-light">(GST inclusive)</span></span>
            <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
          </div>
        </div>

        {lowTime ? (
          /* Under-2-minute safeguard — not enough time to pay; reselect for a fresh hold */
          <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-4 py-4 mb-2">
            <p className="font-dm-sans text-sm font-medium text-white mb-1.5">
              Your hold is about to expire
            </p>
            <p className="font-dm-sans text-sm font-light text-white/65 leading-relaxed mb-4">
              There isn't enough time left to complete payment safely. Please select your cruise again to start a fresh 10-minute hold.
            </p>
            <button
              type="button"
              onClick={exitFlow}
              className="w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
            >
              Reselect cruise
            </button>
          </div>
        ) : (
          <div>
            {errorMsg && (
              <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-3 text-center">
                {errorMsg}
              </p>
            )}
            <button
              type="button"
              onClick={handlePay}
              disabled={phase === 'working' || phase === 'verifying' || phase === 'booking'}
              className={`w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                phase === 'working' || phase === 'verifying' || phase === 'booking'
                  ? 'opacity-60 cursor-not-allowed'
                  : 'opacity-100 active:scale-[0.98] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] cursor-pointer'
              }`}
            >
              {phase === 'booking'
                ? 'Confirming your booking...'
                : phase === 'verifying'
                ? 'Verifying payment...'
                : phase === 'working'
                ? 'Opening secure checkout...'
                : `Pay ₹${grandTotal.toLocaleString()}`}
            </button>
            <p className="font-dm-sans text-center text-sm text-white/55 mt-3 leading-relaxed">
              Payments are processed securely by Razorpay
            </p>
            <button
              type="button"
              onClick={exitFlow}
              className="block mx-auto mt-4 mb-2 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
            >
              Cancel and return to cruise selection
            </button>
          </div>
        )}

      </div>
  );
}
